import React, { memo, useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  AlertCircle, AlertTriangle, Info, CheckCircle, 
  X, ChevronDown, ChevronUp, Lightbulb, Shield
} from 'lucide-react';
import * as Dialog from '@radix-ui/react-dialog';
import useFlowStore from '../store/flowStore';
import { FlowValidator, ValidationResult, ValidationError } from '../utils/flowValidator';

interface FlowValidationProps {
  isOpen: boolean;
  onClose: () => void;
}

const FlowValidation: React.FC<FlowValidationProps> = memo(({ isOpen, onClose }) => {
  const { nodes, edges } = useFlowStore();
  const [validationResult, setValidationResult] = useState<ValidationResult | null>(null);
  const [expandedSections, setExpandedSections] = useState<string[]>(['errors', 'warnings']);
  
  useEffect(() => {
    if (isOpen) {
      const validator = new FlowValidator(nodes, edges);
      const result = validator.validate();
      setValidationResult(result);
    }
  }, [isOpen, nodes, edges]);
  
  const toggleSection = (section: string) => {
    setExpandedSections(prev =>
      prev.includes(section)
        ? prev.filter(s => s !== section)
        : [...prev, section]
    );
  };
  
  const getIcon = (type: ValidationError['type']) => {
    switch (type) {
      case 'error':
        return <AlertCircle className="text-red-500" size={20} />;
      case 'warning':
        return <AlertTriangle className="text-yellow-500" size={20} />;
      case 'info':
        return <Info className="text-blue-500" size={20} />;
    }
  };
  
  const renderValidationItem = (item: ValidationError) => (
    <motion.div
      initial={{ opacity: 0, x: -20 }}
      animate={{ opacity: 1, x: 0 }}
      className="p-3 bg-gray-700 rounded-lg"
    >
      <div className="flex items-start gap-3">
        {getIcon(item.type)}
        <div className="flex-1">
          <p className="text-white text-sm">{item.message}</p>
          {item.suggestion && (
            <p className="text-gray-400 text-xs mt-1 flex items-center gap-1">
              <Lightbulb size={12} />
              {item.suggestion}
            </p>
          )}
          {item.nodeId && (
            <p className="text-gray-500 text-xs mt-1">
              ID Nodo: {item.nodeId}
            </p>
          )}
        </div>
      </div>
    </motion.div>
  );
  
  const renderSection = (
    title: string, 
    items: ValidationError[], 
    icon: React.ReactNode,
    colorClass: string
  ) => {
    if (items.length === 0) return null;
    
    const isExpanded = expandedSections.includes(title.toLowerCase());
    
    return (
      <div className="mb-4">
        <button
          onClick={() => toggleSection(title.toLowerCase())}
          className="w-full flex items-center justify-between p-3 bg-gray-800 rounded-lg hover:bg-gray-700 transition-colors"
        >
          <div className="flex items-center gap-2">
            {icon}
            <span className={`font-medium ${colorClass}`}>{title}</span>
            <span className="text-xs text-gray-400">({items.length})</span>
          </div>
          {isExpanded ? <ChevronUp size={18} /> : <ChevronDown size={18} />}
        </button>
        
        <AnimatePresence>
          {isExpanded && (
            <motion.div
              initial={{ height: 0 }}
              animate={{ height: 'auto' }}
              exit={{ height: 0 }}
              className="overflow-hidden"
            >
              <div className="mt-2 space-y-2">
                {items.map((item, index) => (
                  <div key={index}>{renderValidationItem(item)}</div>
                ))}
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    );
  };
  
  return (
    <Dialog.Root open={isOpen} onOpenChange={onClose}>
      <Dialog.Portal>
        <Dialog.Overlay className="fixed inset-0 bg-black/50 z-[9998]" />
        <Dialog.Content className="fixed top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 z-[9999] w-[90vw] max-w-2xl max-h-[80vh]">
          <Dialog.Title className="sr-only">Flow Validation</Dialog.Title>
          <motion.div
            initial={{ scale: 0.95, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            exit={{ scale: 0.95, opacity: 0 }}
            className="bg-gray-900 rounded-2xl shadow-2xl overflow-hidden"
          >
            {/* Header */}
            <div className="p-6 border-b border-gray-800 flex items-center justify-between">
              <div className="flex items-center gap-3">
                <Shield className="text-whatsapp-green" size={24} />
                <h2 className="text-xl font-bold text-white">Validazione Flusso</h2>
              </div>
              <button
                onClick={onClose}
                className="text-gray-400 hover:text-white transition-colors"
              >
                <X size={24} />
              </button>
            </div>
            
            {/* Content */}
            <div className="p-6 overflow-y-auto max-h-[60vh]">
              {validationResult && (
                <>
                  {/* Overall Status */}
                  <div className={`p-4 rounded-lg mb-6 ${
                    validationResult.isValid 
                      ? 'bg-green-900/20 border border-green-700' 
                      : 'bg-red-900/20 border border-red-700'
                  }`}>
                    <div className="flex items-center gap-3">
                      {validationResult.isValid ? (
                        <>
                          <CheckCircle className="text-green-500" size={24} />
                          <div>
                            <h3 className="text-green-500 font-medium">Flusso Valido</h3>
                            <p className="text-gray-400 text-sm mt-1">
                              Il tuo flusso è configurato correttamente e pronto per essere attivato.
                            </p>
                          </div>
                        </>
                      ) : (
                        <>
                          <AlertCircle className="text-red-500" size={24} />
                          <div>
                            <h3 className="text-red-500 font-medium">Problemi Rilevati</h3>
                            <p className="text-gray-400 text-sm mt-1">
                              Correggi gli errori prima di attivare il flusso.
                            </p>
                          </div>
                        </>
                      )}
                    </div>
                  </div>
                  
                  {/* Validation Results */}
                  {renderSection(
                    'Errori',
                    validationResult.errors,
                    <AlertCircle className="text-red-500" size={20} />,
                    'text-red-500'
                  )}
                  
                  {renderSection(
                    'Avvisi',
                    validationResult.warnings,
                    <AlertTriangle className="text-yellow-500" size={20} />,
                    'text-yellow-500'
                  )}
                  
                  {renderSection(
                    'Suggerimenti',
                    validationResult.suggestions,
                    <Lightbulb className="text-blue-500" size={20} />,
                    'text-blue-500'
                  )}
                  
                  {/* Quick Stats */}
                  <div className="mt-6 p-4 bg-gray-800 rounded-lg">
                    <h4 className="text-white font-medium mb-3">Statistiche Flusso</h4>
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <p className="text-gray-400 text-sm">Nodi totali</p>
                        <p className="text-white text-lg font-medium">{nodes.length}</p>
                      </div>
                      <div>
                        <p className="text-gray-400 text-sm">Connessioni</p>
                        <p className="text-white text-lg font-medium">{edges.length}</p>
                      </div>
                      <div>
                        <p className="text-gray-400 text-sm">Trigger</p>
                        <p className="text-white text-lg font-medium">
                          {nodes.filter(n => n.type === 'trigger').length}
                        </p>
                      </div>
                      <div>
                        <p className="text-gray-400 text-sm">Azioni</p>
                        <p className="text-white text-lg font-medium">
                          {nodes.filter(n => n.type === 'action').length}
                        </p>
                      </div>
                    </div>
                  </div>
                </>
              )}
            </div>
            
            {/* Footer */}
            <div className="p-6 border-t border-gray-800 flex justify-end gap-3">
              <button
                onClick={onClose}
                className="px-4 py-2 text-gray-400 hover:text-white transition-colors"
              >
                Chiudi
              </button>
              {validationResult?.isValid && (
                <button
                  onClick={() => {
                    // Qui puoi aggiungere la logica per attivare il flusso
                    onClose();
                  }}
                  className="px-4 py-2 bg-whatsapp-green text-white rounded-lg hover:bg-whatsapp-dark transition-colors"
                >
                  Attiva Flusso
                </button>
              )}
            </div>
          </motion.div>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
});

FlowValidation.displayName = 'FlowValidation';

export default FlowValidation;
